/**
 * Z-Index Hierarchy Constants
 * Centralized z-index values for consistent layering across the app
 *
 * ╔════════════════════════════════════════════════════════════════════╗
 * ║                    Z-INDEX HIERARCHY                               ║
 * ╠════════════════════════════════════════════════════════════════════╣
 * ║ LAYER 1: Content (0-20)                                            ║
 * ║   - Base content, timeline, task cards                             ║
 * ╠════════════════════════════════════════════════════════════════════╣
 * ║ LAYER 2: Navigation (30-50)                                        ║
 * ║   - Top header, bottom nav bar, FAB                                ║
 * ╠════════════════════════════════════════════════════════════════════╣
 * ║ LAYER 3: Overlays (100-150)                                        ║
 * ║   - Bottom sheets, basic overlays, dropdowns                       ║
 * ╠════════════════════════════════════════════════════════════════════╣
 * ║ LAYER 4: Modals (200-250)                                          ║
 * ║   - Standard modals: Stats, Gallery, Proof Capture, Privacy        ║
 * ╠════════════════════════════════════════════════════════════════════╣
 * ║ LAYER 5: Priority Modals (260-300)                                 ║
 * ║   - Important modals: Settings, Mood Check-in                      ║
 * ╠════════════════════════════════════════════════════════════════════╣
 * ║ LAYER 6: Immersive (310-360)                                       ║
 * ║   - Full-screen: Focus Mode, Crisis Mode                           ║
 * ╠════════════════════════════════════════════════════════════════════╣
 * ║ LAYER 7: Toasts (400-450)                                          ║
 * ║   - Notifications, encouragement messages                          ║
 * ╠════════════════════════════════════════════════════════════════════╣
 * ║ LAYER 8: Celebrations (500-600)                                    ║
 * ║   - XP animations, level up, badges, confetti                      ║
 * ╚════════════════════════════════════════════════════════════════════╝
 */

export const Z_INDEX = {
  // ═══════════════════════════════════════════════════════════════════
  // LAYER 1: Content (0-20) - Main app content
  // ═══════════════════════════════════════════════════════════════════
  BASE: 0,
  TIMELINE_LINE: 5,
  TASK_CARD: 10,
  TASK_CARD_ACTIVE: 15,
  TASK_ACTIONS: 20,

  // ═══════════════════════════════════════════════════════════════════
  // LAYER 2: Navigation (30-50) - Fixed navigation elements
  // ═══════════════════════════════════════════════════════════════════
  TOP_STATUS_BAR: 40,
  BOTTOM_NAV_BAR: 40,
  FAB_BUTTON: 45,
  DROPDOWN: 50,
  NOTIFICATION_CENTER: 55,

  // ═══════════════════════════════════════════════════════════════════
  // LAYER 3: Overlays (100-150) - Basic overlays and sheets
  // ═══════════════════════════════════════════════════════════════════
  BOTTOM_SHEET_BACKDROP: 100,
  BOTTOM_SHEET: 110,
  ADD_TASK_SHEET: 120,

  // ═══════════════════════════════════════════════════════════════════
  // LAYER 4: Modals (200-250) - Standard modal dialogs
  // ═══════════════════════════════════════════════════════════════════
  MODAL_BACKDROP: 200,
  STATS_PANEL: 210,
  VICTORY_GALLERY: 210,
  VICTORY_GALLERY_DETAIL: 220,
  FOCUS_START_MODAL: 210,
  PROOF_CAPTURE: 210,
  PRIVACY_POLICY: 210,

  // ═══════════════════════════════════════════════════════════════════
  // LAYER 5: Priority Modals (260-300) - Important modals
  // ═══════════════════════════════════════════════════════════════════
  SETTINGS_SHEET: 260,
  MOOD_CHECK_IN: 270,

  // ═══════════════════════════════════════════════════════════════════
  // LAYER 6: Immersive (310-360) - Full-screen experiences
  // ═══════════════════════════════════════════════════════════════════
  FOCUS_MODE_OVERLAY: 320,
  GO_SPORT_WIZARD: 320,
  CRISIS_MODE: 350,

  // ═══════════════════════════════════════════════════════════════════
  // LAYER 7: Toasts (400-450) - Notifications
  // ═══════════════════════════════════════════════════════════════════
  TOAST: 400,
  ENCOURAGEMENT_TOAST: 410,

  // ═══════════════════════════════════════════════════════════════════
  // LAYER 8: Celebrations (500-600) - XP and achievements
  // ═══════════════════════════════════════════════════════════════════
  XP_ANIMATION: 500,
  LEVEL_UP: 510,
  BADGE_UNLOCK: 520,
  CONFETTI: 550,
};

/**
 * CSS class names for z-index (Tailwind-style)
 * Use these in className attributes
 */
export const Z_CLASS = {
  // Layer 1: Content
  BASE: 'z-0',
  TIMELINE_LINE: 'z-[5]',
  TASK_CARD: 'z-10',
  TASK_CARD_ACTIVE: 'z-[15]',
  TASK_ACTIONS: 'z-20',

  // Layer 2: Navigation
  TOP_STATUS_BAR: 'z-40',
  BOTTOM_NAV_BAR: 'z-40',
  FAB_BUTTON: 'z-[45]',
  DROPDOWN: 'z-50',
  NOTIFICATION_CENTER: 'z-[55]',

  // Layer 3: Overlays
  BOTTOM_SHEET_BACKDROP: 'z-[100]',
  BOTTOM_SHEET: 'z-[110]',
  ADD_TASK_SHEET: 'z-[120]',

  // Layer 4: Modals
  MODAL_BACKDROP: 'z-[200]',
  STATS_PANEL: 'z-[210]',
  VICTORY_GALLERY: 'z-[210]',
  VICTORY_GALLERY_DETAIL: 'z-[220]',
  FOCUS_START_MODAL: 'z-[210]',
  PROOF_CAPTURE: 'z-[210]',
  PRIVACY_POLICY: 'z-[210]',

  // Layer 5: Priority Modals
  SETTINGS_SHEET: 'z-[260]',
  MOOD_CHECK_IN: 'z-[270]',

  // Layer 6: Immersive
  FOCUS_MODE_OVERLAY: 'z-[320]',
  GO_SPORT_WIZARD: 'z-[320]',
  CRISIS_MODE: 'z-[350]',

  // Layer 7: Toasts
  TOAST: 'z-[400]',
  ENCOURAGEMENT_TOAST: 'z-[410]',

  // Layer 8: Celebrations
  XP_ANIMATION: 'z-[500]',
  LEVEL_UP: 'z-[510]',
  BADGE_UNLOCK: 'z-[520]',
  CONFETTI: 'z-[550]',
};

export default Z_INDEX;
